import { firebaseDb, firebaseFuncs } from './firebase'
import { Collection, Functions, PaymentMethod } from '../config/enums'
import constants from '../config/constants'
import { bankAccountConverter } from '../models/BankAccount'
import axios from 'axios'
import { Order } from '../models/Order'
import { getCountryByName } from './countryService'

const hoolahTestPhone = '92341234'

export const getBigMochiAccountDetail = async () => {
  const snap = await firebaseDb
    .collection(Collection.bankAccounts)
    .where('accountName', '==', constants.bigMochiBankAccountName)
    .withConverter(bankAccountConverter)
    .limit(1)
    .get()

  return snap.empty ? undefined : snap.docs[0].data()
}

export const markOrderAsPlaced = async (
  orderId: string,
  paymentMethod: PaymentMethod
) => {
  const placeOrderFunc = firebaseFuncs.httpsCallable(Functions.placeOrder)
  await placeOrderFunc({ id: orderId, paymentMethod })
}

export const createStripePaymentIntent = async (orderId: string) => {
  const placeOrderFunc = firebaseFuncs.httpsCallable(
    Functions.createStripePaymentIntent
  )
  const res = await placeOrderFunc({ orderId })
  return res.data.clientSecret as string
}

export const payWithCredits = async (orderId: string, amount: number) => {
  const payWithCreditsFunc = firebaseFuncs.httpsCallable(
    Functions.payWithCredits
  )
  await payWithCreditsFunc({ orderId, amount })
}

export const getHoolahPayUrl = async (order: Order) => {
  const isLocal = process.env.NODE_ENV !== 'production'

  const hoolahAPIUrl = process.env.REACT_APP_HOOLAH_API_URL
  const hoolahLadingUrl = process.env.REACT_APP_HOOLAH_LANDING_URL
  const bigMochiAPIUrl = process.env.REACT_APP_BIGMOCHI_API_URL
  const bigMochiWebUrl = process.env.REACT_APP_BIGMOCHI_WEB_URL

  // Step 1: authenticate with hoolah to get authToken
  const authRes = await axios.post(`${hoolahAPIUrl}/auth/login`, {
    username: process.env.REACT_APP_HOOLAH_MERCHANT_USER,
    password: process.env.REACT_APP_HOOLAH_MERCHANT_PASS
  })

  // Step 1.5: Prepare order data to be sent to hoolah
  const recipientCountry = await getCountryByName(
    order.recipient.address.country
  )
  const billerCountry = await getCountryByName(order.biller.address.country)

  let totalShippingFee = 0
  const items: any[] = []
  order.orderItems.forEach(oItem => {
    items.push({
      name: oItem.item.name,
      quantity: oItem.quantity,
      price: oItem.totalPrice,
      sku: oItem.item.id,
      ean: '',
      description: oItem.item.variantName,
      detailedDescription: '',
      originalPrice: oItem.totalPrice,
      taxAmount: 0,
      images: [
        {
          imageLocation: oItem.item.thumbImageUrl
        }
      ],
      merchantItemId: oItem.item.id,
      warranty: 0,
      discount: 0
    })

    totalShippingFee += oItem.shippingInfo.fee
  })

  let shippingAddress = {
    line1: order.recipient.address.address1,
    line2: order.recipient.address.address2,
    line3: '',
    suburb: '',
    postcode: order.recipient.address.zipCode,
    countryCode: recipientCountry?.code ?? 'SG',
    city: order.recipient.address.city,
    province: order.recipient.address.region,
    state: '',
    county: ''
  }
  if (recipientCountry?.code !== 'SG') {
    // Use biller SG address as shipping address so hoolah does not reject us
    shippingAddress = {
      line1: order.biller.address.address1,
      line2: order.biller.address.address2,
      line3: '',
      suburb: '',
      postcode: order.biller.address.zipCode,
      countryCode: billerCountry?.code ?? 'SG',
      city: order.biller.address.city,
      province: order.biller.address.region,
      state: '',
      county: ''
    }
  }

  const params = {
    consumerTitle: '',
    consumerFirstName: order.biller.firstName,
    consumerMiddleName: '',
    consumerLastName: order.biller.lastName,
    consumerEmail: order.biller.email,
    consumerPhoneNumber: isLocal ? hoolahTestPhone : order.biller.phone,
    consumerPhoneNumberExtension: 65,
    shippingAddress,
    billingAddress: {
      line1: order.biller.address.address1,
      line2: order.biller.address.address2,
      line3: '',
      suburb: '',
      postcode: order.biller.address.zipCode,
      countryCode: billerCountry?.code ?? 'SG',
      city: order.biller.address.city,
      province: order.biller.address.region,
      state: '',
      county: ''
    },
    items,
    totalAmount: order.totalAmount,
    originalAmount: order.totalAmount,
    taxAmount: 0,
    cartId: order.id,
    orderType: 'ONLINE',
    shippingAmount: totalShippingFee,
    shippingMethod: 'NORMAL',
    discount: 0,
    voucherCode: '',
    currency: 'SGD',
    callbackUrl: `${bigMochiAPIUrl}/payments-hoolahWebhook`,
    returnToShopUrl: `${bigMochiWebUrl}/shop/confirmation`,
    closeUrl: `${bigMochiWebUrl}/shop/checkout/pay`
  }

  // Step 2: authenticate with hoolah to get authToken
  const orderRes = await axios.post(`${hoolahAPIUrl}/order/initiate`, params, {
    headers: {
      Authorization: `Bearer ${authRes.data.token}`
    }
  })

  return (
    `${hoolahLadingUrl}/` +
    `?ORDER_CONTEXT_TOKEN=${orderRes.data.orderContextToken}` +
    `&platform=bespoke` +
    `&version=1.01`
  )
}
